<template>
  <nav
    class="bottom-tab-bar"
    :class="{ 'bottom-tab-bar--single-post': isSinglePostMode }"
  >
    <!-- Home Tab -->
    <router-link
      :to="homeTo"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isHomeActive }"
      @click="handleHomeClick"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-icon :size="24">{{
          isHomeActive ? "mdi-home" : "mdi-home-outline"
        }}</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Home</span>
    </router-link>

    <!-- Search Tab -->
    <router-link
      to="/search"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isSearchActive }"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-icon :size="24">mdi-magnify</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Search</span>
    </router-link>

    <!-- Center Create Button -->
    <button
      class="bottom-tab-bar__create-btn"
      type="button"
      :aria-label="createButtonLabel"
      @click="handleCreateClick"
    >
      <v-icon size="28">mdi-plus</v-icon>
    </button>

    <!-- Notifications Tab -->
    <router-link
      to="/notifications"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isNotificationsActive }"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-badge
          v-if="unreadNotifications > 0"
          :content="unreadNotifications > 99 ? '99+' : unreadNotifications"
          color="error"
          offset-x="-2"
          offset-y="2"
        >
          <v-icon :size="24">{{
            isNotificationsActive ? "mdi-bell" : "mdi-bell-outline"
          }}</v-icon>
        </v-badge>
        <v-icon v-else :size="24">{{
          isNotificationsActive ? "mdi-bell" : "mdi-bell-outline"
        }}</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Alerts</span>
    </router-link>

    <!-- Discover -->
    <router-link
      to="/discover"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isDiscoverActive }"
      @click="handleDiscoverClick"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-icon :size="24">{{
          isDiscoverActive ? "mdi-compass" : "mdi-compass-outline"
        }}</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Discover</span>
    </router-link>
  </nav>

  <!-- Create Options Bottom Sheet -->
  <Teleport to="body">
    <Transition name="sheet">
      <v-theme-provider
        v-if="showCreateSheet"
        :theme="currentTheme"
        with-background
      >
        <div class="create-sheet-overlay" @click.self="closeCreateSheet">
          <div
            ref="createSheetRef"
            class="create-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-sheet-title"
            tabindex="-1"
            @click.stop
          >
            <h2 id="create-sheet-title" class="sr-only">Create content</h2>
            <div class="create-sheet__handle" />
            <div class="create-sheet__options">
              <button
                ref="createPostButtonRef"
                class="create-sheet__option"
                type="button"
                @click="handleCreatePost"
              >
                <div
                  class="create-sheet__option-icon create-sheet__option-icon--post"
                >
                  <v-icon size="24">mdi-pencil-outline</v-icon>
                </div>
                <div class="create-sheet__option-content">
                  <span class="create-sheet__option-title">Create Post</span>
                  <span class="create-sheet__option-desc"
                    >Share thoughts, photos, or videos</span
                  >
                </div>
              </button>
              <button
                v-if="isMusicMode"
                class="create-sheet__option"
                type="button"
                @click="handleUploadMusic"
              >
                <div
                  class="create-sheet__option-icon create-sheet__option-icon--music"
                >
                  <v-icon size="24">mdi-music-note-plus</v-icon>
                </div>
                <div class="create-sheet__option-content">
                  <span class="create-sheet__option-title">Upload Song</span>
                  <span class="create-sheet__option-desc"
                    >Share your music with the world</span
                  >
                </div>
              </button>
            </div>
          </div>
        </div>
      </v-theme-provider>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { useRoute } from "vue-router";
import { useDialogA11y } from "@/composables/useDialogA11y";
import { useAppSettings } from "@/composables/useAppSettings";
import { useUiStore } from "@/stores/ui";
import { useCreatePostContext } from "@/composables/useCreatePostContext";
import type { OpenCreatePostOptions } from "@/types";

interface Props {
  isSinglePostMode?: boolean;
}

withDefaults(defineProps<Props>(), {
  isSinglePostMode: false,
});

const route = useRoute();
const { isMusicMode } = useAppSettings();
const uiStore = useUiStore();
const { getGroupContext } = useCreatePostContext();

// Inject create functions from App.vue
const openCreatePost = inject<
  ((options?: OpenCreatePostOptions) => void) | undefined
>("openCreatePost", undefined);
const openCreateSong = inject<() => void>("openCreateSong");

// Create options bottom sheet (mobile)
const showCreateSheet = ref(false);
const currentTheme = computed(() => uiStore.effectiveTheme);
const createSheetRef = ref<HTMLElement | null>(null);
const createPostButtonRef = ref<HTMLButtonElement | null>(null);

// TODO: Add notification badge count from a notification store
const unreadNotifications = computed(() => 0);

// Navigation targets
const homeTo = "/timeline";

// Active state computations
const isHomeActive = computed(
  () => route.path.startsWith("/timeline") || route.path === "/",
);

const isSearchActive = computed(() => route.path.startsWith("/search"));

const isNotificationsActive = computed(() =>
  route.path.startsWith("/notifications"),
);

const isDiscoverActive = computed(
  () => route.name === "discover" || route.name === "discover-category",
);

// When only one create option exists (Create Post), go straight to it; otherwise show sheet
const hasMultipleCreateOptions = computed(() => isMusicMode.value);
const createButtonLabel = computed(() =>
  hasMultipleCreateOptions.value ? "Create content" : "Create post",
);

function openCreatePostWithRouteContext() {
  openCreatePost?.(getGroupContext());
}

// Tapping home while already on a feed page scrolls to top
function handleHomeClick() {
  if (isHomeActive.value) {
    const singlePostFeed = document.querySelector('.single-post-feed') as HTMLElement;
    if (singlePostFeed && singlePostFeed.scrollTop > 0) {
      singlePostFeed.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
}

function handleCreateClick() {
  if (!hasMultipleCreateOptions.value) {
    openCreatePostWithRouteContext();
    return;
  }
  showCreateSheet.value = true;
}

function closeCreateSheet() {
  showCreateSheet.value = false;
}

function handleDiscoverClick() {
  if (!isDiscoverActive.value) return;
  // Discover content scrolls inside its own container (`.discover-view__feed-zone`
  // has `overflow-y: auto`), not the window — so target that element and fall
  // back to the window only if it isn't present (e.g. reel-mode layout).
  const scrollEl = document.querySelector<HTMLElement>(
    ".discover-view__feed-zone",
  );
  if (scrollEl) {
    scrollEl.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

function handleCreatePost() {
  closeCreateSheet();
  openCreatePostWithRouteContext();
}

function handleUploadMusic() {
  closeCreateSheet();
  openCreateSong?.();
}

useDialogA11y({
  isOpen: showCreateSheet,
  containerRef: createSheetRef,
  onClose: closeCreateSheet,
  focusInitial: () => {
    createPostButtonRef.value?.focus();
  },
});
</script>

<style lang="scss" scoped>
.bottom-tab-bar {
  // Use raw env() so the device-reported safe area drives bar height (no 34px floor).
  --bottom-tab-safe-area: env(safe-area-inset-bottom, 0px);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--z-bottom-tab);
  display: flex;
  // Center icons across the full bar height (icons + home-indicator zone) so the
  // safe-area padding does not visually read as a dead "gap" below the tabs.
  align-items: center;
  justify-content: space-around;
  height: calc(56px + var(--bottom-tab-safe-area));
  padding-bottom: 0;
  // Liquid glass: match header (strong blur + saturation, subtle edge highlight)
  background: rgba(12, 10, 9, 0.35);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  // border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.06);
  transition: all 0.3s var(--ease-out-expo);

  // Right corner: same as left but mirrored (circle at top-right, rotate -90deg)
  &::before {
    content: "";
    position: absolute;
    top: -20px;
    right: 0;
    width: 20px;
    height: 20px;
    background: radial-gradient(
      circle at 100% 0,
      transparent 0,
      transparent 20px,
      rgba(12, 10, 9, 0.35) 20px
    );
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    pointer-events: none;
    transform: rotate(-90deg);
  }

  // Left corner: background only to the left of the curve; right of curve (bite) transparent
  &::after {
    content: "";
    position: absolute;
    top: -20px;
    left: 0;
    width: 20px;
    height: 20px;
    background: radial-gradient(
      circle at 0 0,
      transparent 0,
      transparent 20px,
      rgba(12, 10, 9, 0.35) 20px
    );
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    pointer-events: none;
    transform: rotate(90deg);
  }

  &__tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    height: 56px;
    padding: 6px 0;
    text-decoration: none;
    color: rgba(var(--v-theme-on-surface), 0.6);
    transition: color 0.2s ease;

    &:hover {
      color: rgba(var(--v-theme-on-surface), 0.9);
    }

    &--active {
      color: rgb(var(--v-theme-primary));

      .bottom-tab-bar__icon-container,
      .bottom-tab-bar__icon-container .v-icon {
        color: rgb(var(--v-theme-primary)) !important;
      }

      .bottom-tab-bar__label {
        font-weight: 600;
      }
    }
  }

  &__icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
  }

  &__label {
    font-size: 10px;
    font-weight: 500;
    margin-top: 2px;
    font-family: var(--font-body);
  }

  &__create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    margin: 0 8px;
    border: 2px solid transparent;
    border-radius: 14px;
    // Gradient border: light top-left corner, dark bottom-right (3D effect)
    background:
      linear-gradient(
          rgb(var(--v-theme-background)),
          rgb(var(--v-theme-background))
        )
        padding-box,
      linear-gradient(
          135deg,
          rgb(var(--v-theme-primary-accent)) 0%,
          rgb(var(--v-theme-primary-accent)) 22%,
          rgb(var(--v-theme-primary)) 45%,
          rgb(var(--v-theme-secondary)) 78%,
          rgb(var(--v-theme-secondary)) 100%
        )
        border-box;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.35);
    }

    &:active {
      transform: scale(0.95);
    }
  }
}

@supports (padding-bottom: env(safe-area-inset-bottom)) {
  @media (display-mode: standalone) and (max-width: 960px) {
    .bottom-tab-bar {
      &__tab {
        height: calc(56px + min(var(--bottom-tab-safe-area), 10px));
        padding-bottom: min(var(--bottom-tab-safe-area), 10px);
      }

      &__create-btn {
        margin-bottom: min(var(--bottom-tab-safe-area), 10px);
      }
    }
  }
}

// Create sheet overlay
.create-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-mobile-menu);
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

// Create sheet
.create-sheet {
  width: 100%;
  max-width: 500px;
  background: rgb(var(--v-theme-surface));
  border-radius: 20px 20px 0 0;
  padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);

  &__handle {
    width: 36px;
    height: 4px;
    margin: 0 auto 16px;
    background: rgba(var(--v-theme-on-surface), 0.2);
    border-radius: 2px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 16px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 16px;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
    }

    &:active {
      background: rgba(var(--v-theme-on-surface), 0.15);
    }
  }

  &__option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    flex-shrink: 0;

    &--post {
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      color: white;
    }

    &--music {
      background: linear-gradient(135deg, #f59e0b, #d97706);
      color: white;
    }
  }

  &__option-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__option-title {
    font-size: 1rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  &__option-desc {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
}

// Sheet transition
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.25s ease;

  .create-sheet {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;

  .create-sheet {
    transform: translateY(100%);
  }
}
</style>
